import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Button, Card, Col, Descriptions, Empty, Input, Row, Segmented, Select, Space, Spin, Tag, Typography, Upload, message, theme,
} from 'antd';
import { LinkOutlined, PaperClipOutlined, ReloadOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import AttachmentImages from '../../../shared/components/AttachmentImages';
import supportAdminService from '../../services/supportAdminService';
import UserDetailDrawer from '../UserDetailDrawer';
import { adminPath } from '../../../shared/config/paths';
import { useTranslation } from '../../../shared/i18n';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { uploadFiles, patchFile } from '../../../shared/utils/directUpload';
import {
    ok, STATUS_LABEL, STATUS_COLOR, PRIORITY_LABEL, PRIORITY_COLOR, fmtTime, fmtSize, resourceRoute, parseSystemBody,
} from './common';

const { Text, Paragraph } = Typography;
const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.zip,.json,.csv';
const FIELD_LABEL = { status: '状态', priority: '优先级', department: '部门', assignee: '处理人' };

const TicketDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const [meta, setMeta] = useState(null);
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState('');
    const [mode, setMode] = useState('reply');
    const [body, setBody] = useState('');
    const [after, setAfter] = useState('answered');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [userOpen, setUserOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await supportAdminService.getTicket(id);
        setLoading(false);
        if (ok(r)) setD(r.data); else message.error(r?.msg || t('Failed to load'));
    }, [id, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { supportAdminService.getMeta().then((r) => { if (ok(r)) setMeta(r.data); }); }, []);

    const deptName = useMemo(() => Object.fromEntries((meta?.departments || []).map((x) => [String(x.id), x.name])), [meta]);
    const adminName = useMemo(() => Object.fromEntries((meta?.admins || []).map((x) => [String(x.id), x.username || x.email])), [meta]);

    const tk = d?.ticket;
    const internal = mode === 'note';

    const update = async (field, value) => {
        setSaving(field);
        const r = await supportAdminService.update(id, { [field]: value });
        setSaving('');
        if (ok(r)) { message.success(t('Updated')); load(); } else message.error(r?.msg || t('Failed to save'));
    };

    const send = async () => {
        if (!body.trim()) return;
        setSending(true);
        let attachments;
        try {
            attachments = await uploadFiles(files, supportAdminService.getUploadUrl, (uid, patch) => setFiles((list) => patchFile(list, uid, patch)));
        } catch (e) {
            setSending(false);
            message.error(e?.msg || t('Send failed'));
            return;
        }
        const r = await supportAdminService.reply(id, { body, internal, status: after, attachments });
        setSending(false);
        if (ok(r)) {
            message.success(internal ? t('Note added') : t('Answered'));
            setBody(''); setFiles([]); setAfter('answered');
            load();
        } else message.error(r?.msg || t('Send failed'));
    };

    const download = async (att) => {
        const r = await supportAdminService.downloadAttachment(att);
        if (!ok(r)) message.error(r?.msg || t('Download failed'));
    };

    const sysValue = (field, v) => {
        if (v === undefined || v === null || v === '' || v === '-') return field === 'assignee' ? t('Not assigned') : '-';
        if (field === 'status') return t(STATUS_LABEL[v] || v);
        if (field === 'priority') return t(PRIORITY_LABEL[v] || v);
        if (field === 'department') return deptName[v] || `#${v}`;
        if (field === 'assignee') return adminName[v] || `#${v}`;
        return v;
    };

    // customer_closed 是客户动作，不显示管理员名
    const c0 = (changes) => !(changes.length === 1 && changes[0].field === 'customer_closed');

    const renderSystem = (m) => {
        const changes = parseSystemBody(m.body);
        const who = m.author_id ? (adminName[String(m.author_id)] || `#${m.author_id}`) : '';
        return (
            <div key={m.id} style={{ textAlign: 'center', margin: '8px 0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {changes.map((c, i) => (
                        <span key={i}>
                            {i > 0 && ' · '}
                            {c.field === 'customer_closed'
                                ? t('Customer closed the ticket')
                                : FIELD_LABEL[c.field]
                                    ? `${t(FIELD_LABEL[c.field])} ${sysValue(c.field, c.from)} → ${sysValue(c.field, c.to)}`
                                    : (c.to || '')}
                        </span>
                    ))}
                    {who && c0(changes) && ` · ${who}`}
                    {` · ${fmtTime(m.created_at)}`}
                </Text>
            </div>
        );
    };
    const renderMessage = (m) => {
        if (m.author_type === 'system') return renderSystem(m);
        const staff = m.author_type === 'admin';
        const note = staff && m.internal;
        const bg = note ? token.colorWarningBg : staff ? token.colorPrimaryBg : token.colorFillAlter;
        const border = note ? token.colorWarningBorder : staff ? token.colorPrimaryBorder : token.colorBorderSecondary;
        const name = staff ? (m.author_name || adminName[String(m.author_id)] || t('Support')) : (d?.customer?.email || t('Customer'));
        return (
            <div key={m.id} style={{ display: 'flex', justifyContent: staff ? 'flex-end' : 'flex-start', margin: '12px 0' }}>
                <div style={{ maxWidth: '85%', minWidth: 240, background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px' }}>
                    <Space size={8} style={{ marginBottom: 6 }} wrap>
                        <Text strong>{name}</Text>
                        {note ? <Tag color="gold">{t('Internal note')}</Tag> : staff ? <Tag color="blue">{t('Support')}</Tag> : <Tag>{t('Customer')}</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(m.created_at)}</Text>
                    </Space>
                    <Paragraph style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>{m.body}</Paragraph>
                    <AttachmentImages items={m.attachments} />
                    {(m.attachments || []).some((a) => !a.preview_url) && (
                        <Space wrap size={4} style={{ marginTop: 8 }}>
                            {m.attachments.filter((a) => !a.preview_url).map((a) => (
                                <Button key={a.id} size="small" icon={<PaperClipOutlined />} onClick={() => download(a)}>
                                    {a.filename} <Text type="secondary" style={{ fontSize: 12 }}>{fmtSize(a.size)}</Text>
                                </Button>
                            ))}
                        </Space>
                    )}
                </div>
            </div>
        );
    };

    if (!d) {
        return <div style={{ padding: 48, textAlign: 'center' }}>{loading ? <Spin /> : <Empty />}</div>;
    }

    const route = resourceRoute(tk.service_type, tk.resource_id);
    const cust = d.customer;
    const statusOpts = (meta?.statuses || ['open', 'answered', 'closed']).map((s) => ({ value: s, label: t(STATUS_LABEL[s] || s) }));

    return (
        <div>
            <PageHeader
                onBack={() => navigate(-1)}
                title={<Space size={8}><Text code>{tk.number}</Text><span>{tk.subject}</span></Space>}
                tags={<Space size={4}>
                    <Tag color={STATUS_COLOR[tk.status]}>{t(STATUS_LABEL[tk.status] || tk.status)}</Tag>
                    {tk.priority !== 'normal' && <Tag color={PRIORITY_COLOR[tk.priority]}>{t(PRIORITY_LABEL[tk.priority] || tk.priority)}</Tag>}
                </Space>}
                extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />}
            />
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card size="small">
                        {(d.messages || []).map(renderMessage)}
                    </Card>
                    <Card size="small" style={{ marginTop: 16, background: internal ? token.colorWarningBg : undefined }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Segmented value={mode} onChange={setMode} options={[
                                { value: 'reply', label: t('Reply to customer') },
                                { value: 'note', label: t('Internal note') },
                            ]} />
                            <Input.TextArea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                                placeholder={internal ? t('Visible to staff only') : ''}
                                onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') send(); }} />
                            <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Upload multiple accept={ACCEPT} fileList={files} maxCount={5} disabled={sending}
                                    beforeUpload={() => false} onChange={({ fileList }) => setFiles(fileList.slice(0, 5))}>
                                    <Button size="small" icon={<UploadOutlined />}>{t('Attachments')}</Button>
                                </Upload>
                                <Space>
                                    {!internal && (
                                        <Space size={4}>
                                            <Text type="secondary">{t('After reply')}</Text>
                                            <Select size="small" style={{ width: 100 }} value={after} onChange={setAfter} options={statusOpts} />
                                        </Space>
                                    )}
                                    <Button type="primary" icon={<SendOutlined />} loading={sending} disabled={!body.trim()} onClick={send}>
                                        {internal ? t('Add note') : t('Send')}
                                    </Button>
                                </Space>
                            </Space>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Card size="small" title={t('Ticket properties')}>
                            <Descriptions column={1} size="small" styles={{ label: { width: 72 } }}>
                                <Descriptions.Item label={t('Status')}>
                                    <Select size="small" style={{ width: '100%' }} value={tk.status} loading={saving === 'status'}
                                        onChange={(v) => update('status', v)} options={statusOpts} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Priority')}>
                                    <Select size="small" style={{ width: '100%' }} value={tk.priority} loading={saving === 'priority'}
                                        onChange={(v) => update('priority', v)}
                                        options={(meta?.priorities || ['low', 'normal', 'high', 'urgent']).map((p) => ({ value: p, label: t(PRIORITY_LABEL[p] || p) }))} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Department')}>
                                    <Select size="small" style={{ width: '100%' }} value={tk.department_id ? Number(tk.department_id) : undefined}
                                        loading={saving === 'department_id'} onChange={(v) => update('department_id', v)}
                                        options={(meta?.departments || []).map((x) => ({ value: Number(x.id), label: x.name }))} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Assignee')}>
                                    <Select size="small" style={{ width: '100%' }} allowClear placeholder={t('Not assigned')}
                                        value={tk.assigned_admin_id ? Number(tk.assigned_admin_id) : undefined}
                                        loading={saving === 'assigned_admin_id'} onChange={(v) => update('assigned_admin_id', v || null)}
                                        options={(meta?.admins || []).map((a) => ({ value: Number(a.id), label: a.username || a.email }))} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Create')}>{fmtTime(tk.created_at)}</Descriptions.Item>
                                {tk.closed_at && <Descriptions.Item label={t('Close')}>{fmtTime(tk.closed_at)}</Descriptions.Item>}
                            </Descriptions>
                        </Card>

                        <Card size="small" title={t('Customer')}
                            extra={cust && <Button type="link" size="small" onClick={() => setUserOpen(true)}>{t('User details')}</Button>}>
                            {cust ? (
                                <Descriptions column={1} size="small" styles={{ label: { width: 72 } }}>
                                    <Descriptions.Item label={t('Email')}><Text copyable>{cust.email}</Text></Descriptions.Item>
                                    {cust.username && <Descriptions.Item label={t('Username')}>{cust.username}</Descriptions.Item>}
                                    <Descriptions.Item label={t('Balance')}>{formatMoney(parseFloat(cust.balance) || 0, PLATFORM_CURRENCY)}</Descriptions.Item>
                                    <Descriptions.Item label={t('Status')}><Tag color={cust.status === 'active' ? 'green' : 'default'}>{cust.status}</Tag></Descriptions.Item>
                                    <Descriptions.Item label={t('Register')}>{fmtTime(cust.created_at)}</Descriptions.Item>
                                </Descriptions>
                            ) : <Text type="secondary">#{tk.user_id}</Text>}
                        </Card>

                        {tk.service_type && (
                            <Card size="small" title={t('Related service')}>
                                <Space direction="vertical" size={4}>
                                    <Space size={6} wrap>
                                        <Tag>{t(tk.service_type_label || tk.service_type)}</Tag>
                                        <Text strong>{tk.resource_label || (tk.resource_id ? `#${tk.resource_id}` : '-')}</Text>
                                        {tk.resource_exists === false && <Tag color="red">{t('Deleted')}</Tag>}
                                    </Space>
                                    {route && (
                                        <Link to={adminPath(route.path)}>
                                            <LinkOutlined /> {route.detail ? t('Open details') : t('Go to list')}
                                        </Link>
                                    )}
                                </Space>
                            </Card>
                        )}

                        <Card size="small" title={t('Customer\'s other tickets')}
                            extra={<Link to={adminPath(`/support/tickets?status=all&user_id=${tk.user_id}`)}>{t('All')}</Link>}>
                            {(d.other_tickets || []).length === 0 ? <Text type="secondary">{t('None')}</Text> : (
                                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                    {d.other_tickets.map((o) => (
                                        <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                            <Link to={adminPath(`/support/tickets/${o.id}`)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {o.number} · {o.subject}
                                            </Link>
                                            <Tag color={STATUS_COLOR[o.status]} style={{ marginRight: 0 }}>{t(STATUS_LABEL[o.status] || o.status)}</Tag>
                                        </div>
                                    ))}
                                </Space>
                            )}
                        </Card>
                    </Space>
                </Col>
            </Row>
            <UserDetailDrawer visible={userOpen} user={cust} onClose={() => setUserOpen(false)} onChanged={load} />
        </div>
    );
};

export default TicketDetail;
